import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Megaphone, 
  Target,
  DollarSign,
  BarChart3,
  FileText,
  Zap,
  ArrowLeft,
  RefreshCw,
  Copy,
  Calculator,
  TrendingUp,
  Crown,
  Lock,
  Construction,
  Sparkles
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AIProgressLoader from "@/components/premium/AIProgressLoader";
import useUser from "@/hooks/useUser";
import { PremiumLock, LoginRequired } from "@/components/PremiumLock";
import RelatedTools from "@/components/RelatedTools";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Tabs that are coming soon (need API integration)
const COMING_SOON_TABS = [];

function GoogleAdsToolsContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, plan, isPremium, isLoggedIn, loading: userLoading } = useUser();
  const [activeTool, setActiveTool] = useState("keyword-planner");

  // Get tool from URL params
  useEffect(() => {
    const toolFromUrl = searchParams.get('tool');
    if (toolFromUrl) {
      setActiveTool(toolFromUrl);
    }
  }, [searchParams]);

  // Keyword Planner State
  const [keywords, setKeywords] = useState("");
  const [keywordResults, setKeywordResults] = useState(null);
  const [loadingKeywords, setLoadingKeywords] = useState(false);

  // Ad Copy Generator State
  const [adTopic, setAdTopic] = useState("");
  const [adType, setAdType] = useState("google_ads");
  const [adBusiness, setAdBusiness] = useState("");
  const [adBusinessDesc, setAdBusinessDesc] = useState("");
  const [adUsp, setAdUsp] = useState("");
  const [adFeatures, setAdFeatures] = useState("");
  const [adAudience, setAdAudience] = useState("");
  const [adTone, setAdTone] = useState("Professional");
  const [adGoal, setAdGoal] = useState("conversion");
  const [generatedAds, setGeneratedAds] = useState(null);
  const [loadingAds, setLoadingAds] = useState(false);

  // CPC Calculator State
  const [budget, setBudget] = useState("");
  const [cpc, setCpc] = useState("");
  const [conversionRate, setConversionRate] = useState("");
  const [cpcResults, setCpcResults] = useState(null);

  // Campaign Tracker State (NEW)
  const [campaignData, setCampaignData] = useState(null);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [dateRange, setDateRange] = useState("LAST_30_DAYS");
  const [customerAccounts, setCustomerAccounts] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [accountSummary, setAccountSummary] = useState(null);

  const googleAdsTools = [
    {
      id: "keyword-planner",
      name: "Keyword Planner",
      icon: <Target className="w-5 h-5" />,
      description: "Find keywords for your Google Ads campaigns",
      premium: false
    },
    {
      id: "ad-copy-generator",
      name: "Ad Copy Generator",
      icon: <FileText className="w-5 h-5" />,
      description: "Generate high-converting ad copy with AI",
      premium: false
    },
    {
      id: "cpc-calculator",
      name: "CPC Calculator",
      icon: <Calculator className="w-5 h-5" />,
      description: "Calculate cost-per-click and ROI",
      premium: false
    },
    {
      id: "competitor-ads",
      name: "Competitor Ads",
      icon: <BarChart3 className="w-5 h-5" />,
      description: "Analyze competitor ad strategies",
      premium: false
    },
    {
      id: "quality-score",
      name: "Quality Score Optimizer",
      icon: <Zap className="w-5 h-5" />,
      description: "Improve your ad quality score",
      premium: false
    },
    {
      id: "campaign-tracker",
      name: "Campaign Tracker",
      icon: <TrendingUp className="w-5 h-5" />,
      description: "Track campaign performance from Google Ads",
      premium: true
    }
  ];

  // Fetch customer accounts from Google Ads API
  const fetchCustomerAccounts = async () => {
    setLoadingCustomers(true);
    try {
      const response = await axios.get(`${API}/google-ads/customers`, {
        withCredentials: true
      });
      setCustomerAccounts(response.data.customers || []);
      if (response.data.default_customer_id) {
        setSelectedCustomerId(response.data.default_customer_id);
      } else if (response.data.customers?.length > 0) {
        setSelectedCustomerId(response.data.customers[0].customer_id);
      }
    } catch (error) {
      console.error("Failed to fetch customer accounts:", error);
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Fetch campaign data from Google Ads API
  const fetchCampaignData = async () => {
    if (!selectedCustomerId) {
      toast.error("Please select a Google Ads account");
      return;
    }
    setLoadingCampaigns(true);
    try {
      const response = await axios.get(`${API}/google-ads/performance`, {
        params: { 
          date_range: dateRange,
          customer_id: selectedCustomerId 
        },
        withCredentials: true
      });
      setCampaignData(response.data);
      
      // Also fetch account summary
      const summaryResponse = await axios.get(`${API}/google-ads/summary`, {
        params: { 
          date_range: dateRange,
          customer_id: selectedCustomerId 
        },
        withCredentials: true
      });
      setAccountSummary(summaryResponse.data.summary);
      
      toast.success("Campaign data loaded!");
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Premium feature - Please upgrade to Pro");
      } else {
        toast.error(error.response?.data?.detail || "Failed to load campaigns");
      }
    } finally {
      setLoadingCampaigns(false);
    }
  };

  // Load customer accounts when Campaign Tracker is selected
  useEffect(() => {
    if (activeTool === "campaign-tracker" && isPremium && isLoggedIn && customerAccounts.length === 0) {
      fetchCustomerAccounts();
    }
  }, [activeTool, isPremium, isLoggedIn]);

  const handleKeywordResearch = async () => {
    if (!keywords.trim()) {
      toast.error("Please enter keywords");
      return;
    }

    setLoadingKeywords(true);
    try {
      const response = await axios.post(`${API}/keywords/research`, {
        keyword: keywords.trim()
      });
      setKeywordResults(response.data);
      toast.success("Keyword research complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to research keywords");
    } finally {
      setLoadingKeywords(false);
    }
  };

  const handleGenerateAds = async () => {
    if (!adTopic.trim() || !adBusiness.trim()) {
      toast.error("Please enter both business name and product name");
      return;
    }

    setLoadingAds(true);
    setGeneratedAds(null);
    try {
      const response = await axios.post(`${API}/ai-ads/generate`, {
        business_name: adBusiness,
        business_description: adBusinessDesc,
        product_name: adTopic,
        product_features: adFeatures.split(",").map((f) => f.trim()).filter(Boolean),
        usp: adUsp,
        target_audience: adAudience,
        tone: adTone,
        campaign_goal: adGoal,
        platform: adType,
      });
      setGeneratedAds(response.data);
      toast.success(`${response.data.headlines?.length || 0} headlines generated!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate ads");
    } finally {
      setLoadingAds(false);
    }
  };

  const calculateCPC = () => {
    const budgetNum = parseFloat(budget);
    const cpcNum = parseFloat(cpc);
    const convRate = parseFloat(conversionRate) / 100;

    if (!budgetNum || !cpcNum) {
      toast.error("Please enter budget and CPC");
      return;
    }

    const clicks = Math.floor(budgetNum / cpcNum);
    const conversions = Math.floor(clicks * (convRate || 0.02));
    const costPerConversion = conversions > 0 ? budgetNum / conversions : 0;

    setCpcResults({
      budget: budgetNum,
      cpc: cpcNum,
      clicks,
      conversions,
      costPerConversion: costPerConversion.toFixed(2),
      conversionRate: ((convRate || 0.02) * 100).toFixed(1)
    });
    toast.success("Calculation complete!");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getDifficultyColor = (difficulty) => {
    if (difficulty < 30) return "bg-green-100 text-green-800";
    if (difficulty < 50) return "bg-yellow-100 text-yellow-800";
    if (difficulty < 70) return "bg-orange-100 text-orange-800";
    return "bg-red-100 text-red-800";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-4 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4">Google Ads Tools</h3>
              <div className="space-y-2">
                {googleAdsTools.map((tool) => (
                  <button
                    key={tool.id}
                    onClick={() => setActiveTool(tool.id)}
                    data-testid={`tool-${tool.id}`}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                      activeTool === tool.id
                        ? 'bg-green-100 text-green-700 font-medium'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {tool.icon}
                        <span className="text-sm">{tool.name}</span>
                      </div>
                      {tool.premium && !isPremium && (
                        <Lock className="w-4 h-4 text-violet-500" />
                      )}
                      {tool.premium && isPremium && (
                        <Crown className="w-4 h-4 text-yellow-500" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Keyword Planner */}
            {activeTool === "keyword-planner" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Keyword Planner</h2>
                    <p className="text-gray-600">Find high-performing keywords for your ads</p>
                  </div>
                </div>

                <div className="flex gap-4 mb-6">
                  <Input
                    placeholder="Enter seed keywords (e.g., digital marketing agency)"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    className="flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && handleKeywordResearch()}
                    data-testid="keyword-input"
                  />
                  <Button 
                    onClick={handleKeywordResearch}
                    disabled={loadingKeywords}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="research-keywords-btn"
                  >
                    {loadingKeywords ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Searching...</>
                    ) : "Find Keywords"}
                  </Button>
                </div>

                {keywordResults && (
                  <div className="space-y-4">
                    <h3 className="font-semibold">
                      Found {keywordResults.total_variations} Keywords
                    </h3>

                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold">Keyword</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold">Competition</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold">Avg. Monthly Searches</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold">CPC Estimate</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {keywordResults.keywords.slice(0, 15).map((kw, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm">{kw.keyword}</td>
                              <td className="px-4 py-3">
                                <Badge className={getDifficultyColor(kw.difficulty)}>
                                  {kw.category}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {kw.estimated_monthly_searches.toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-green-600">
                                ${kw.cpc_estimate}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Ad Copy Generator — AI-enhanced with business/product context */}
            {activeTool === "ad-copy-generator" && (
              <Card className="p-6 rounded-3xl border-slate-200">
                <div className="flex items-center gap-3 mb-6 pb-6 border-b border-slate-100">
                  <div className="w-11 h-11 bg-[#111111] rounded-2xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs tracking-[0.18em] font-semibold text-[#A855F7] uppercase mb-0.5">AI Ad Copy Generator</p>
                    <h2 className="text-xl font-semibold text-[#111111]">Context-aware Google Ads copy</h2>
                    <p className="text-sm text-slate-500">Powered by Claude Sonnet 4.5 — enter business + product context for tuned output</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="grid md:grid-cols-2 gap-3">
                    <Input placeholder="Business name (e.g. Acme SaaS)" value={adBusiness} onChange={(e) => setAdBusiness(e.target.value)} className="rounded-xl" data-testid="ad-business-name"/>
                    <Input placeholder="Product name (e.g. TeamSync Pro)" value={adTopic} onChange={(e) => setAdTopic(e.target.value)} className="rounded-xl" data-testid="ad-topic-input"/>
                  </div>

                  <Input placeholder="What does your business do? (one line)" value={adBusinessDesc} onChange={(e) => setAdBusinessDesc(e.target.value)} className="rounded-xl" data-testid="ad-business-desc"/>

                  <Input placeholder="Unique selling proposition (e.g. 'the only Slack for accountants')" value={adUsp} onChange={(e) => setAdUsp(e.target.value)} className="rounded-xl" data-testid="ad-usp"/>

                  <Input placeholder="Key features (comma separated: fast, secure, 24/7 support…)" value={adFeatures} onChange={(e) => setAdFeatures(e.target.value)} className="rounded-xl" data-testid="ad-features"/>

                  <Input placeholder="Target audience (e.g. small business owners in India)" value={adAudience} onChange={(e) => setAdAudience(e.target.value)} className="rounded-xl" data-testid="ad-audience"/>

                  <div className="grid md:grid-cols-3 gap-3">
                    <Select value={adTone} onValueChange={setAdTone}>
                      <SelectTrigger className="rounded-xl" data-testid="ad-tone-select"><SelectValue placeholder="Tone" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Professional">Professional</SelectItem>
                        <SelectItem value="Playful">Playful</SelectItem>
                        <SelectItem value="Urgent">Urgent</SelectItem>
                        <SelectItem value="Luxury">Luxury</SelectItem>
                        <SelectItem value="Casual">Casual</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={adGoal} onValueChange={setAdGoal}>
                      <SelectTrigger className="rounded-xl" data-testid="ad-goal-select"><SelectValue placeholder="Campaign goal" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="conversion">Conversions</SelectItem>
                        <SelectItem value="traffic">Website Traffic</SelectItem>
                        <SelectItem value="awareness">Brand Awareness</SelectItem>
                        <SelectItem value="brand">Brand Building</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={adType} onValueChange={setAdType}>
                      <SelectTrigger className="rounded-xl" data-testid="ad-type-select"><SelectValue placeholder="Platform" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="google_ads">Google Ads</SelectItem>
                        <SelectItem value="facebook_ads">Facebook Ads</SelectItem>
                        <SelectItem value="linkedin_ads">LinkedIn Ads</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={handleGenerateAds}
                    disabled={loadingAds}
                    className="w-full rounded-full bg-[#A855F7] hover:bg-[#9333EA] text-white py-6"
                    data-testid="generate-ads-btn"
                  >
                    {loadingAds ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> AI is crafting your ads…</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Generate AI ad copy</>
                    )}
                  </Button>
                </div>

                {loadingAds && (
                  <div className="my-4" data-testid="ad-loading">
                    <AIProgressLoader
                      visible={loadingAds}
                      label="crafting your ad copy"
                      stages={[
                        "reading your business + product context",
                        "researching angle and hook",
                        "writing headline variants",
                        "checking character limits",
                      ]}
                    />
                  </div>
                )}

                {generatedAds && generatedAds.headlines && (
                  <div className="space-y-5 pt-6 border-t border-slate-100" data-testid="ad-result">
                    {generatedAds.best_variant && (
                      <div className="p-5 rounded-2xl bg-[#FAF5FF] border border-[#A855F7]/20">
                        <p className="text-xs tracking-[0.18em] font-semibold text-[#A855F7] uppercase mb-2">Best variant</p>
                        <p className="text-lg font-semibold text-[#111111]">{generatedAds.best_variant.headline}</p>
                        <p className="text-sm text-slate-700 mt-1">{generatedAds.best_variant.description}</p>
                        <p className="text-xs text-slate-500 mt-3 italic">💡 {generatedAds.best_variant.reasoning}</p>
                      </div>
                    )}

                    <div>
                      <h4 className="text-sm font-semibold text-[#111111] mb-2">15 Headlines <span className="text-xs text-slate-500 font-normal">(each ≤ 30 chars)</span></h4>
                      <div className="grid md:grid-cols-3 gap-2">
                        {generatedAds.headlines.map((h, i) => (
                          <div key={i} className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-sm text-[#111111] flex items-center justify-between gap-2">
                            <span className="truncate">{h}</span>
                            <span className="text-[10px] text-slate-400 shrink-0">{h.length}c</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-[#111111] mb-2">4 Descriptions <span className="text-xs text-slate-500 font-normal">(each ≤ 90 chars)</span></h4>
                      <div className="space-y-2">
                        {generatedAds.descriptions?.map((d, i) => (
                          <div key={i} className="px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-sm text-slate-700 flex items-start justify-between gap-2">
                            <span>{d}</span>
                            <span className="text-[10px] text-slate-400 shrink-0 mt-0.5">{d.length}c</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {generatedAds.callouts?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-[#111111] mb-2">Callouts</h4>
                        <div className="flex flex-wrap gap-2">
                          {generatedAds.callouts.map((c, i) => (
                            <span key={i} className="px-3 py-1 rounded-full bg-slate-100 text-[#111111] text-xs">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {generatedAds.sitelinks?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-[#111111] mb-2">Sitelinks</h4>
                        <div className="grid md:grid-cols-2 gap-2">
                          {generatedAds.sitelinks.map((sl, i) => (
                            <div key={i} className="p-3 rounded-xl border border-slate-200 bg-white">
                              <p className="text-sm font-semibold text-[#A855F7]">{sl.title}</p>
                              {sl.description1 && <p className="text-xs text-slate-600 mt-0.5">{sl.description1}</p>}
                              {sl.description2 && <p className="text-xs text-slate-600">{sl.description2}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <span className="text-[11px] text-slate-400">Generated by {generatedAds.model || 'Claude Sonnet 4.5'} · {generatedAds.headlines?.length || 0} headlines · {generatedAds.descriptions?.length || 0} descriptions</span>
                      <Button variant="outline" size="sm" className="rounded-full" onClick={() => copyToClipboard(JSON.stringify(generatedAds, null, 2))}>
                        <Copy className="w-3.5 h-3.5 mr-1" /> Copy JSON
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* CPC Calculator */}
            {activeTool === "cpc-calculator" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                    <Calculator className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">CPC Calculator</h2>
                    <p className="text-gray-600">Calculate your ad campaign metrics</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Monthly Budget ($)</label>
                    <Input
                      type="number"
                      placeholder="e.g., 1000"
                      value={budget}
                      onChange={(e) => setBudget(e.target.value)}
                      data-testid="budget-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Average CPC ($)</label>
                    <Input
                      type="number"
                      placeholder="e.g., 2.50"
                      value={cpc}
                      onChange={(e) => setCpc(e.target.value)}
                      data-testid="cpc-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Conversion Rate (%)</label>
                    <Input
                      type="number"
                      placeholder="e.g., 2 (default)"
                      value={conversionRate}
                      onChange={(e) => setConversionRate(e.target.value)}
                      data-testid="conversion-rate-input"
                    />
                  </div>
                </div>

                <Button 
                  onClick={calculateCPC}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  data-testid="calculate-btn"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate
                </Button>

                {cpcResults && (
                  <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-sm text-gray-600">Monthly Budget</p>
                      <p className="text-2xl font-bold text-blue-600">${cpcResults.budget}</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <p className="text-sm text-gray-600">Expected Clicks</p>
                      <p className="text-2xl font-bold text-green-600">{cpcResults.clicks}</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                      <p className="text-sm text-gray-600">Expected Conversions</p>
                      <p className="text-2xl font-bold text-purple-600">{cpcResults.conversions}</p>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg text-center">
                      <p className="text-sm text-gray-600">Cost Per Conversion</p>
                      <p className="text-2xl font-bold text-orange-600">${cpcResults.costPerConversion}</p>
                    </div>
                    <div className="p-4 bg-pink-50 rounded-lg text-center">
                      <p className="text-sm text-gray-600">Conversion Rate</p>
                      <p className="text-2xl font-bold text-pink-600">{cpcResults.conversionRate}%</p>
                    </div>
                    <div className="p-4 bg-cyan-50 rounded-lg text-center">
                      <p className="text-sm text-gray-600">CPC</p>
                      <p className="text-2xl font-bold text-cyan-600">${cpcResults.cpc}</p>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Campaign Tracker - PREMIUM */}
            {activeTool === "campaign-tracker" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-bold text-gray-900">Campaign Tracker</h2>
                      <Badge className="bg-violet-100 text-violet-700">
                        <Crown className="w-3 h-3 mr-1" />
                        Pro
                      </Badge>
                    </div>
                    <p className="text-gray-600">Track your Google Ads campaign performance</p>
                  </div>
                </div>

                {/* Check if user is logged in */}
                {!isLoggedIn && !userLoading && (
                  <LoginRequired message="Please login to access the Campaign Tracker." />
                )}

                {/* Check if user has premium access */}
                {isLoggedIn && !isPremium && (
                  <PremiumLock 
                    feature="Campaign Tracker"
                    description="Connect your Google Ads account and view real-time campaign performance, clicks, conversions, and ROI. Upgrade to Pro to unlock this feature."
                    showCard={false}
                  />
                )}

                {/* Show tool if user has access */}
                {isLoggedIn && isPremium && (
                  <>
                    {/* Account Selection and Controls */}
                    <div className="flex flex-wrap items-center gap-4 mb-6">
                      {/* Customer Account Selector */}
                      <div className="flex-1 min-w-[200px]">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Google Ads Account</label>
                        <Select 
                          value={selectedCustomerId} 
                          onValueChange={setSelectedCustomerId}
                          disabled={loadingCustomers}
                        >
                          <SelectTrigger data-testid="customer-select">
                            <SelectValue placeholder={loadingCustomers ? "Loading accounts..." : "Select Account"} />
                          </SelectTrigger>
                          <SelectContent>
                            {customerAccounts.map((acc) => (
                              <SelectItem key={acc.customer_id} value={acc.customer_id}>
                                {acc.name || `Account ${acc.customer_id}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Date Range */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                        <Select value={dateRange} onValueChange={setDateRange}>
                          <SelectTrigger className="w-[180px]" data-testid="date-range-select">
                            <SelectValue placeholder="Select date range" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="LAST_7_DAYS">Last 7 Days</SelectItem>
                            <SelectItem value="LAST_14_DAYS">Last 14 Days</SelectItem>
                            <SelectItem value="LAST_30_DAYS">Last 30 Days</SelectItem>
                            <SelectItem value="THIS_MONTH">This Month</SelectItem>
                            <SelectItem value="LAST_MONTH">Last Month</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Fetch Button */}
                      <div className="self-end">
                        <Button 
                          onClick={fetchCampaignData}
                          disabled={loadingCampaigns || !selectedCustomerId}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="fetch-campaigns-btn"
                        >
                          {loadingCampaigns ? (
                            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
                          ) : (
                            <><RefreshCw className="w-4 h-4 mr-2" /> Fetch Data</>
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Account Summary Cards */}
                    {accountSummary && (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                        <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg text-center">
                          <p className="text-xs text-gray-600 mb-1">Impressions</p>
                          <p className="text-xl font-bold text-blue-700">{accountSummary.impressions?.toLocaleString()}</p>
                        </div>
                        <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg text-center">
                          <p className="text-xs text-gray-600 mb-1">Clicks</p>
                          <p className="text-xl font-bold text-green-700">{accountSummary.clicks?.toLocaleString()}</p>
                        </div>
                        <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg text-center">
                          <p className="text-xs text-gray-600 mb-1">CTR</p>
                          <p className="text-xl font-bold text-purple-700">{accountSummary.ctr}%</p>
                        </div>
                        <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg text-center">
                          <p className="text-xs text-gray-600 mb-1">Cost</p>
                          <p className="text-xl font-bold text-orange-700">${accountSummary.cost?.toFixed(2)}</p>
                        </div>
                        <div className="p-4 bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg text-center">
                          <p className="text-xs text-gray-600 mb-1">Conversions</p>
                          <p className="text-xl font-bold text-pink-700">{accountSummary.conversions?.toFixed(0)}</p>
                        </div>
                        <div className="p-4 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg text-center">
                          <p className="text-xs text-gray-600 mb-1">Conv. Value</p>
                          <p className="text-xl font-bold text-cyan-700">${accountSummary.conversion_value?.toFixed(2)}</p>
                        </div>
                      </div>
                    )}

                    {campaignData && (
                      <div className="space-y-6">
                        {campaignData.is_demo && (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                            ⚠️ Showing demo data. Your Google Ads API is now approved - connect a real account to see live data.
                          </div>
                        )}

                        {!campaignData.is_demo && (
                          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                            ✅ Connected to Google Ads API (Basic Access) - Showing real data for account {selectedCustomerId}
                          </div>
                        )}

                        {/* Campaign Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead>
                              <tr className="bg-gray-50">
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Campaign</th>
                                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Status</th>
                                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Impressions</th>
                                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Clicks</th>
                                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">CTR</th>
                                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Cost</th>
                                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Conversions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {campaignData.campaigns.map((campaign, idx) => (
                                <tr key={idx} className="border-t hover:bg-gray-50">
                                  <td className="px-4 py-3 font-medium">{campaign.name}</td>
                                  <td className="px-4 py-3 text-right">
                                    <Badge className={campaign.status === "ENABLED" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                                      {campaign.status}
                                    </Badge>
                                  </td>
                                  <td className="px-4 py-3 text-right">{campaign.impressions?.toLocaleString()}</td>
                                  <td className="px-4 py-3 text-right">{campaign.clicks?.toLocaleString()}</td>
                                  <td className="px-4 py-3 text-right">{campaign.ctr}%</td>
                                  <td className="px-4 py-3 text-right">${campaign.cost?.toFixed(2)}</td>
                                  <td className="px-4 py-3 text-right">{campaign.conversions}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* No campaigns message */}
                        {campaignData.campaigns.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            No campaigns found for this date range. Try selecting a different date range or account.
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </Card>
            )}

            {/* Competitor Ads Analysis */}
            {activeTool === "competitor-ads" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Competitor Ads Analysis</h2>
                    <p className="text-gray-600">Analyze competitor ad strategies and keywords</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Competitor Domain</label>
                    <Input placeholder="e.g., competitor.com" />
                  </div>

                  <Button className="w-full bg-red-600 hover:bg-red-700">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Analyze Competitor Ads
                  </Button>

                  {/* Demo Results */}
                  <div className="space-y-4">
                    <h3 className="font-semibold">Sample Competitor Analysis</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Active Ads</p>
                        <p className="text-2xl font-bold text-blue-600">24</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Keywords Targeting</p>
                        <p className="text-2xl font-bold text-green-600">156</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Est. Monthly Spend</p>
                        <p className="text-2xl font-bold text-purple-600">$12.5K</p>
                      </div>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium mb-3">Top Performing Ads</h4>
                      <div className="space-y-3">
                        {[
                          { headline: "Best Marketing Tools 2025", ctr: "4.2%", position: 1.3 },
                          { headline: "Free SEO Audit - Get Started", ctr: "3.8%", position: 1.5 },
                          { headline: "Grow Your Business Online", ctr: "3.5%", position: 2.1 }
                        ].map((ad, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-white rounded border">
                            <div>
                              <p className="font-medium text-blue-600">{ad.headline}</p>
                              <p className="text-xs text-gray-500">Avg. Position: {ad.position}</p>
                            </div>
                            <Badge variant="outline">{ad.ctr} CTR</Badge>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium mb-3">Top Keywords</h4>
                      <div className="flex flex-wrap gap-2">
                        {["digital marketing", "seo tools", "content marketing", "social media", "ppc advertising", "email marketing"].map((kw, idx) => (
                          <Badge key={idx} variant="secondary">{kw}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Quality Score Optimizer */}
            {activeTool === "quality-score" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Quality Score Optimizer</h2>
                    <p className="text-gray-600">Improve your Google Ads quality score</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-blue-800 mb-2">What is Quality Score?</h4>
                    <p className="text-sm text-blue-700">
                      Quality Score is Google&rsquo;s rating of the quality and relevance of your keywords and PPC ads.
                      It affects your ad rank and cost per click. Higher scores = lower costs & better positions.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Your Keyword</label>
                    <Input placeholder="Enter your target keyword" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Current Landing Page URL</label>
                    <Input placeholder="https://yoursite.com/landing-page" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Ad Copy Headline</label>
                    <Input placeholder="Your current ad headline" />
                  </div>

                  <Button className="w-full bg-yellow-600 hover:bg-yellow-700">
                    <Zap className="w-4 h-4 mr-2" />
                    Analyze Quality Score
                  </Button>

                  {/* Demo Score Display */}
                  <div className="text-center p-8 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg">
                    <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-full shadow-lg mb-4">
                      <span className="text-4xl font-bold text-green-600">7</span>
                      <span className="text-lg text-gray-500">/10</span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900">Good Quality Score</p>
                    <p className="text-sm text-gray-600">Above average performance</p>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold">Quality Score Breakdown</h4>
                    
                    {[
                      { factor: "Ad Relevance", score: "Above Average", color: "green", tip: "Your ads closely match user search intent" },
                      { factor: "Landing Page Experience", score: "Average", color: "yellow", tip: "Consider improving page load speed" },
                      { factor: "Expected CTR", score: "Above Average", color: "green", tip: "Your ad copy is compelling" }
                    ].map((item, idx) => (
                      <div key={idx} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{item.factor}</span>
                          <Badge className={`${item.color === 'green' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {item.score}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600">{item.tip}</p>
                      </div>
                    ))}
                  </div>

                  <div className="p-4 bg-violet-50 rounded-lg">
                    <h4 className="font-medium mb-3 text-violet-800">Improvement Suggestions</h4>
                    <ul className="text-sm text-violet-700 space-y-2">
                      <li>• Include your target keyword in the ad headline</li>
                      <li>• Ensure landing page content matches ad message</li>
                      <li>• Improve page load speed (target under 3 seconds)</li>
                      <li>• Add more specific ad extensions</li>
                      <li>• Use responsive search ads for better matching</li>
                    </ul>
                  </div>
                </div>
              </Card>
            )}

            {/* Placeholder for other tools */}
            {!["keyword-planner", "ad-copy-generator", "cpc-calculator", "campaign-tracker", "competitor-ads", "quality-score"].includes(activeTool) && (
              <Card className="p-6 text-center">
                <div className="py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {googleAdsTools.find(t => t.id === activeTool)?.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {googleAdsTools.find(t => t.id === activeTool)?.name}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {googleAdsTools.find(t => t.id === activeTool)?.description}
                  </p>
                  <Badge>Coming Soon</Badge>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Related Tools Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <RelatedTools currentToolId={activeTool} category="ads" />
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default GoogleAdsToolsContent;
